import { useEffect, useState } from 'preact/hooks';

interface ConfirmOptions {
  title?: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

let pushFn: ((req: ConfirmRequest) => void) | null = null;

/**
 * Opens a modal confirmation dialog and resolves with true on confirm,
 * false on cancel or backdrop dismiss. Use to gate destructive actions.
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!pushFn) {
      resolve(false);
      return;
    }
    pushFn({...options, resolve});
  });
}

/**
 * Renders the global confirmation dialog. Mount once at the app root.
 */
export function ConfirmHost() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  useEffect(() => {
    pushFn = (req) => setRequest(req);
    return () => { pushFn = null; };
  }, []);

  if (!request) {
    return null;
  }

  const close = (value: boolean) => {
    request.resolve(value);
    setRequest(null);
  };

  const onBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      close(false);
    }
  };

  return (
    <div class="modal-backdrop" onClick={onBackdropClick}>
      <div class="modal" role="dialog" aria-modal="true">
        {request.title && <h2 class="modal-title">{request.title}</h2>}
        <p class="modal-message">{request.message}</p>
        <div class="modal-actions">
          <button type="button" class="btn secondary" onClick={() => close(false)}>
            {request.cancelLabel || 'Abbrechen'}
          </button>
          <button
            type="button"
            class={`btn ${request.destructive ? 'destructive' : ''}`}
            onClick={() => close(true)}
          >
            {request.okLabel || 'Bestätigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
